// =========================================================================
// Helper de normalisation universelle (miroir client_matching backend).
// Règles : metier_config_listes.normalisation_types (chargées via API)
// Préfixes : metier_config_listes.prefixes_ignores_matching
// =========================================================================
//
// Usage typique sur les inputs :
//   <Input
//     value={form.nom}
//     onChange={(e) => setForm({...form, nom: e.target.value})}
//     onBlur={(e) => setForm({...form, nom: normalizeName(e.target.value, "nom")})}
//   />
//
// Backend Pydantic (ClientCreate) refait la normalisation au save —
// le onBlur est cosmétique (feedback visuel immédiat à l'utilisateur).

const DEFAULT_TYPE_RULE = {
  nom:        "UPPER_NOACCENT_TRIM",
  prenom:     "TITLE_LOWER",
  entreprise: "UPPER_NOACCENT_TRIM",
  ville:      "TITLE_LOWER",
  adresse:    "TITLE_FIRST_ONLY",
};

const DEFAULT_PREFIXES = [
  "SAS", "SARL", "SA", "SASU", "SNC", "EURL",
  "GROUPE", "GROUP",
  "MME", "M", "MR", "MADAME", "MONSIEUR",
  "THE", "LES", "LE", "LA", "L'",
];

function stripAccents(s) {
  return s.normalize("NFKD").replace(/[̀-ͯ]/g, "");
}

function titleCase(s) {
  if (!s) return "";
  const out = [];
  for (const word of s.split(/(\s+)/)) {
    if (!word || /^\s+$/.test(word)) { out.push(word); continue; }
    // Acronyme court ALL-CAPS → garder
    if (word.length <= 3 && word === word.toUpperCase() && /^[A-Z]+$/.test(word)) {
      out.push(word);
      continue;
    }
    const lowered = word.toLowerCase();
    out.push(lowered.split(/([-'’])/).map((p) =>
      p && /^[a-zà-ÿ]/i.test(p[0]) ? p[0].toUpperCase() + p.slice(1) : p
    ).join(""));
  }
  return out.join("").trim();
}

function dropPrefixes(s, prefixes = DEFAULT_PREFIXES) {
  let out = s;
  let changed = true;
  while (changed) {
    changed = false;
    for (const p of prefixes) {
      const P = p.toUpperCase();
      for (const sep of [" ", "-", "."]) {
        if (out.startsWith(P + sep)) {
          out = out.slice(P.length + 1).trim();
          changed = true;
          break;
        }
      }
      if (changed) break;
      if (P.endsWith("'") && out.startsWith(P)) {
        out = out.slice(P.length).trim();
        changed = true;
        break;
      }
    }
  }
  return out;
}

const RULES = {
  UPPER_NOACCENT_TRIM:      (s) => stripAccents(s).toUpperCase().trim(),
  TITLE_LOWER:              (s) => titleCase(s.trim()),
  UPPER_NOACCENT_NOPREFIX:  (s, pfx) => dropPrefixes(stripAccents(s).toUpperCase().trim(), pfx),
  TITLE_FIRST_ONLY:         (s) => {
    const t = s.trim();
    return t ? t[0].toUpperCase() + t.slice(1).toLowerCase() : "";
  },
};

/**
 * Normalise une valeur selon le type métier.
 * @param {string|null|undefined} value
 * @param {string} type "nom" | "prenom" | "entreprise" | "ville" | "adresse"
 * @param {object} [options]
 * @param {object} [options.ruleOverrides]
 * @param {string[]} [options.prefixes]
 * @returns {string}
 */
export function normalizeName(value, type, options = {}) {
  if (value == null || value === "") return "";
  const s = String(value);
  const rules = { ...DEFAULT_TYPE_RULE, ...(options.ruleOverrides || {}) };
  const code = rules[type] || type;
  const fn = RULES[code];
  if (!fn) return s.trim();
  return fn(s, options.prefixes || DEFAULT_PREFIXES);
}

/**
 * Comparaison fuzzy (UPPER, sans accents, préfixes droppés pour entreprise).
 */
export function normalizeForMatching(value, type = "nom", options = {}) {
  if (value == null || value === "") return "";
  let base = stripAccents(String(value)).toUpperCase().trim().replace(/\s+/g, " ");
  if (type === "entreprise" || type === "raison_sociale") {
    return dropPrefixes(base, options.prefixes || DEFAULT_PREFIXES);
  }
  return base;
}

/**
 * Charge la config DB une fois et la cache.
 * Appelé idéalement au boot de l'app.
 */
let _cachedConfig = null;
export async function loadNormalizeConfig(api, getHeaders) {
  try {
    const [typesRes, prefixesRes] = await Promise.all([
      fetch(`${api}/api/metier/config-listes/normalisation_types`, { headers: getHeaders(), credentials: "include" }),
      fetch(`${api}/api/metier/config-listes/prefixes_ignores_matching`, { headers: getHeaders(), credentials: "include" }),
    ]);
    const typesItems = typesRes.ok ? (await typesRes.json()).items || [] : [];
    const prefixesItems = prefixesRes.ok ? (await prefixesRes.json()).items || [] : [];
    const ruleMap = { ...DEFAULT_TYPE_RULE };
    for (const it of typesItems) {
      if (it.id && it.rule) ruleMap[it.id] = it.rule;
    }
    const prefixes = prefixesItems
      .map((it) => (it.id || it.label || "").toUpperCase())
      .filter(Boolean);
    _cachedConfig = { types: ruleMap, prefixes: prefixes.length ? prefixes : DEFAULT_PREFIXES };
    return _cachedConfig;
  } catch {
    return { types: DEFAULT_TYPE_RULE, prefixes: DEFAULT_PREFIXES };
  }
}

export function getCachedConfig() {
  return _cachedConfig || { types: DEFAULT_TYPE_RULE, prefixes: DEFAULT_PREFIXES };
}
